// src/pages/LandingPage.js - Updated for invitation-based authentication
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiCall } from '../hooks/useApiCall';
import apiService from '../services/apiService';
import ForgotPasswordModal from '../components/profile/ForgotPasswordModal';

import '../styles/globals.css';

const LandingPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(''); // ADD THIS

  const { login } = useAuth(); // Remove signup from useAuth
  const navigate = useNavigate();
  const location = useLocation();
  
  const { loading, error, makeRequest, clearError } = useApiCall();

    /**
   * Validate password requirements
   * FIXED: Now enforces 8 characters, uppercase, lowercase, and special character
   */
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[!@#$%^&*(),.?":{}|<>-]/.test(password)) {
      return 'Password must contain at least one special character (!@#$%^&*...)';
    }
    return '';
  };


  /**
   * Handle password change with real-time validation
   */
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    // Only validate for signup/activation, not login
    if (!isLogin && newPassword) {
      const error = validatePassword(newPassword);
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  };

  // Handle URL parameters for activation mode
  useEffect(() => {
    console.log('🔍 Full URL:', window.location.href);
    console.log('🔍 location.search:', location.search);
    console.log('🔍 location:', location);
    
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get('mode');
    const emailParam = searchParams.get('email');

    console.log('🔍 Parsed mode:', mode);
    console.log('🔍 Parsed email:', emailParam);

    // If mode=activate, switch to activation view
    if (mode === 'activate') {
      console.log('✅ Switching to activation mode');
      setIsLogin(false);
      
      // Pre-fill email if provided in URL
      if (emailParam) {
        console.log('✅ Pre-filling email:', emailParam);
        setEmail(decodeURIComponent(emailParam));
      }
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      if (isLogin) {
        // Login flow - authenticate with Firebase
        await makeRequest(
          async () => {
            const result = await login(email, password);
            if (!result.success) {
              throw new Error(result.error);
            }
            return result;
          },
          'Signing in...'
        );
        
        navigate('/app');
      } else {
        // Account activation flow - for invited users
        //  VALIDATE PASSWORD FIRST
        const passwordValidation = validatePassword(password);
        if (passwordValidation) {
          setPasswordError(passwordValidation);
          return; // Don't submit if password is invalid
        }
        
        // Call the new activation endpoint
        await makeRequest(
          async () => {
            console.log('Activating invited user account...');
            
            const userData = await apiService.publicPost('/api/v1/users/activate', {
              email: email,
              password: password,
              displayName: displayName || email.split('@')[0],
              token: null
            });

            console.log('Account activated successfully:', userData);
            
            if (userData.success && userData.data.customToken) {
              const { signInWithCustomToken } = await import('firebase/auth');
              const { auth } = await import('../config/firebase');
              await signInWithCustomToken(auth, userData.data.customToken);
            }
            
            return userData;
          },
          'Activating your account...'
        );
        
        navigate('/dashboard');
      }
    } catch (authError) {
      console.error('Authentication error:', authError);

      const code = authError.responseData?.code || authError.message;

      if (code?.includes('INVITATION_NOT_FOUND')) {
        throw new Error('No invitation found for this email address. Please contact your administrator to get invited to the system.');
      } else if (code?.includes('USER_ALREADY_ACTIVE')) {
        throw new Error('This account has already been activated. Please use the sign in option instead.');
      } else if (code?.includes('FIREBASE_EMAIL_EXISTS')) {
        throw new Error('An account with this email already exists. Please use the sign in option.');
      } else if (authError.responseData?.error) {
        throw new Error(authError.responseData.error);
      }

      throw authError;
    }
  };

  return (
    <div className="landing-container">
      {/* Above-the-fold: hero left, auth right */}
      <div className="landing-above-fold">
        <section className="hero">
          <h1 className="hero-title">Better Conversations</h1>
          <h2 className="hero-subtitle">AI-Powered Communication Coaching</h2>
          <p className="hero-description">
            Master professional conversations with personalized AI coaching.
            Whether you&apos;re closing deals, giving feedback, or proposing goals,
            get expert guidance tailored to your specific situation.
          </p>
          <div className="hero-features-mini">
            <span>✓ Sales Conversations</span>
            <span>✓ Management Coaching</span>
            <span>✓ Personalized Guidance</span>
          </div>
        </section>

        {/* Auth Section */}
        <section className="auth-section">
          <div className="auth-container">
          <div className="auth-form">
            <h3 className="auth-form-title">
              {isLogin ? 'Sign In' : 'Activate Your Account'}
            </h3>
            
            {!isLogin && (
              <div className="mb-4 p-3" style={{ 
                backgroundColor: '#eff6ff', 
                border: '1px solid #bfdbfe', 
                borderRadius: 'var(--radius-md)' 
              }}>
                <p className="text-sm" style={{ color: '#1d4ed8' }}>
                  <strong>Have an invitation?</strong> Enter the email address your administrator 
                  invited and create your password to activate your account.
                </p>
              </div>
            )}
            
            {error && (
              <div className="alert alert-error mb-4">
                {error}
                <button 
                  onClick={clearError}
                  className="alert-close"
                >
                  ×
                </button>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email {!isLogin && <span className="text-error">*</span>}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="form-input"
                  placeholder={isLogin ? "your.email@company.com" : "Enter your invited email address"}
                />
                {!isLogin && (
                  <p className="text-xs text-gray-500 mt-1">
                    This must match the email address your administrator used to invite you.
                  </p>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password {!isLogin && <span className="text-error">*</span>}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}  // UPDATED: Use new handler
                  required
                  disabled={loading}
                  minLength="8"  // UPDATED: Changed from 6 to 8
                  className="form-input"
                  placeholder={isLogin ? "Your password" : "Create a secure password (8+ characters)"}
                />
                
                {/* Dynamic password requirements checklist */}
                {!isLogin && (
                  <div className="password-requirements">
                    <p>Requirements:</p>
                    <ul>
                      <li className={password.length >= 8 ? 'valid' : ''}>8+ characters</li>
                      <li className={/[A-Z]/.test(password) ? 'valid' : ''}>Uppercase letter (A-Z)</li>
                      <li className={/[a-z]/.test(password) ? 'valid' : ''}>Lowercase letter (a-z)</li>
                      <li className={/[!@#$%^&*(),.?":{}|<>-]/.test(password) ? 'valid' : ''}>Special character (!@#$%...)</li>
                    </ul>
                  </div>
                )}
            
                {isLogin && (
                  <div className="text-right mt-2">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="link-button text-sm"
                      disabled={loading}
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}
              </div>
   
              {!isLogin && (
                <div className="form-group">
                  <label htmlFor="displayName" className="form-label">
                    Display Name (Optional)
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={loading}
                    className="form-input"
                    placeholder="How you'd like to be shown in the app"
                  />
                </div>
              )}
              
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading || (!isLogin && passwordError)}  // UPDATED: Disable if password invalid
              >
                {loading ? (
                  <div className="flex items-center gap-2 justify-center">
                    <div className="loading-dots" style={{ scale: '0.7' }}>
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                    </div>
                    <span>{isLogin ? 'Signing in...' : 'Activating account...'}</span>
                  </div>
                ) : (
                  isLogin ? 'Sign In' : 'Activate Account'
                )}
              </button>
            </form>
            
            <p className="auth-toggle">
              {isLogin ? "Have an invitation to activate? " : "Already have an active account? "}
              <button 
                type="button"
                className="link-button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setDisplayName('');
                  setPasswordError('');  // UPDATED: Clear password error
                  clearError();
                }}
                disabled={loading}
              >
                {isLogin ? 'Activate account' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
        </section>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default LandingPage;